<template>
  <FriedModal class="invite-success-modal" @close="$emit('close')">
    <template #container>
      <div class="invite-success-container">
        <div class="hero-wrapper">
          <div class="image-container">
            <div class="image" />
          </div>
          <FriedH2 class="header-text">{{ t('confirmation_modal_theyre_invited') }}</FriedH2>
          <div
            v-for="participant in invitation.participants.slice(0, 5)"
            :key="participant.email"
            class="participant-details"
          >
            <FriedH5> {{ formatParticipantNames(participant) }} </FriedH5>
            <FriedParagraph>
              {{ participant.email }}
            </FriedParagraph>
          </div>
          <FriedParagraph
            v-if="invitation.participants.length > 5"
            v-tooltip="{
              content: formatRestOfParticipants(),
              delay: { show: 300, hide: 100 },
              offset: 5,
              placement: Placement.Top,
            }"
            >{{ `+ ${restOfParticipants.length} ${t('dr_invite_five_or_more')}` }}</FriedParagraph
          >
          <div class="message">
            <FriedParagraph v-if="!!invitation.notificationMessage">
              {{ invitation.notificationMessage }}
            </FriedParagraph>
            <FriedParagraph>{{
              t('dr_invite_best_regards', {
                senderName,
              })
            }}</FriedParagraph>
          </div>
          <FriedButton
            data-external="send-more-invitation-button"
            :button-type="ButtonType.Tertiary"
            @click="$emit('invite-more')"
          >
            <template #leftIcon>
              <FriedIcon icon="send" />
            </template>
            {{ t('dr_invite_more') }}</FriedButton
          >
        </div>
      </div>
    </template>
  </FriedModal>
</template>
<script lang="ts">
import { ButtonType, Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import type { InvitePayload, ParticipantInvite } from '../types/participant-invite';

export default defineComponent({
  props: {
    invitation: {
      type: Object as PropType<InvitePayload>,
      required: true,
    },
    senderName: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { t } = useI18n();
    const restOfParticipants = computed(() => {
      const { participants } = props.invitation;
      return participants.slice(5, participants.length);
    });

    const formatRestOfParticipants = () =>
      restOfParticipants.value.length > 0
        ? restOfParticipants.value.map(participant => participant.email).join(',\n')
        : '';

    const formatParticipantNames = (participant: ParticipantInvite) => {
      const { firstName, lastName } = participant;
      if (!firstName && !lastName) {
        return null;
      }
      return `${firstName} ${lastName} | `;
    };

    return {
      formatRestOfParticipants,
      restOfParticipants,
      Placement,
      ButtonType,
      t,
      formatParticipantNames,
    };
  },
});
</script>

<style lang="scss" scoped>
.invite-success-modal {
  .invite-success-container {
    width: 100%;
    background-image: url('../../assets/gradient.png');
    background-repeat: no-repeat;
    background-size: cover;
    background-color: var(--gray-bg);
    background-position: center top;
    padding: var(--spacing-200);
    border-radius: var(--radius-medium);

    .hero-wrapper {
      display: flex;
      flex-direction: column;
      text-align: center;
      align-items: center;
      margin: 0 var(--spacing-200);
      padding: 0 var(--spacing-100);

      @media screen and (max-width: $xs) {
        margin: 0;
        padding: 0 var(--spacing-50);
      }

      .congrats-text {
        margin-bottom: var(--spacing-50);
      }
    }

    .image-container {
      position: relative;
      margin-top: var(--spacing-50);
      margin-bottom: var(--spacing-100);

      .image {
        width: 250px;
        height: 150px;
        background-image: url('../../assets/sparkle.png');
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        position: relative;
        z-index: 1;
      }
    }

    .header-text {
      margin-bottom: var(--spacing-50);
    }

    .participant-details {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: var(--spacing-25);
      gap: var(--spacing-25);

      h5 {
        color: var(--gray-39);
      }
    }
  }

  .message {
    margin: var(--spacing-200) 0 var(--spacing-50);

    p {
      white-space: pre-line;
      color: var(--gray-39);

      &:first-of-type {
        margin-bottom: var(--spacing-50);
      }
    }
  }

  .button {
    margin-top: var(--spacing-200);
    width: 100%;
  }
}
</style>
